import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Button from '../../components/common/Button';
import { api } from '../../services/api';
import { Close } from '@mui/icons-material';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.lg};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
  width: 90%;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  
  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 14px;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: #fee2e2;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const AppVersionForm = ({ isOpen, onClose, version }) => {
  const { isDark } = useTheme();
  const theme = { isDark };

  const [platform, setPlatform] = useState('android');
  const [latestVersion, setLatestVersion] = useState('');
  const [minRequiredVersion, setMinRequiredVersion] = useState('');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [appStoreUrl, setAppStoreUrl] = useState('');
  const [playStoreUrl, setPlayStoreUrl] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (version) {
      setPlatform(version.platform || 'android');
      setLatestVersion(version.latestVersion || '');
      setMinRequiredVersion(version.minRequiredVersion || '');
      setForceUpdate(version.forceUpdate || false);
      setUpdateMessage(version.updateMessage || '');
      setAppStoreUrl(version.appStoreUrl || '');
      setPlayStoreUrl(version.playStoreUrl || '');
    } else {
      resetForm();
    }
  }, [version, isOpen]);

  const resetForm = () => {
    setPlatform('android');
    setLatestVersion('');
    setMinRequiredVersion('');
    setForceUpdate(false);
    setUpdateMessage('Please update to the latest version for the best experience.');
    setAppStoreUrl('');
    setPlayStoreUrl('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const data = {
        platform,
        latestVersion,
        minRequiredVersion,
        forceUpdate,
        updateMessage,
        appStoreUrl,
        playStoreUrl,
      };

      if (version) {
        await api.updateAppVersion(version._id || version.id, data);
      } else {
        await api.createAppVersion(data);
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Xəta baş verdi');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>
            {version ? 'App Version Redaktə Et' : 'Yeni App Version'}
          </ModalTitle>
          <CloseButton theme={theme} onClick={onClose}>
            <Close />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          {error && <ErrorText theme={theme}>{error}</ErrorText>}

          <FormGroup theme={theme}>
            <Label theme={theme}>Platform *</Label>
            <Select
              theme={theme}
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              required
            >
              <option value="android">Android</option>
              <option value="ios">iOS</option>
            </Select>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Latest Version * (məs: 1.0.0)</Label>
            <Input
              theme={theme}
              type="text"
              value={latestVersion}
              onChange={(e) => setLatestVersion(e.target.value)}
              placeholder="1.0.0"
              required
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Min Required Version * (məs: 0.9.0)</Label>
            <Input
              theme={theme}
              type="text"
              value={minRequiredVersion}
              onChange={(e) => setMinRequiredVersion(e.target.value)}
              placeholder="0.9.0"
              required
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme} style={{ display: 'flex', alignItems: 'center', gap: getTheme(isDark).spacing.sm }}>
              <Checkbox
                type="checkbox"
                checked={forceUpdate}
                onChange={(e) => setForceUpdate(e.target.checked)}
              />
              Force Update
            </Label>
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Update Message</Label>
            <Input
              theme={theme}
              type="text"
              value={updateMessage}
              onChange={(e) => setUpdateMessage(e.target.value)}
              placeholder="Please update to the latest version for the best experience."
            />
          </FormGroup>

          {platform === 'ios' && (
            <FormGroup theme={theme}>
              <Label theme={theme}>App Store URL</Label>
              <Input
                theme={theme}
                type="url"
                value={appStoreUrl}
                onChange={(e) => setAppStoreUrl(e.target.value)}
                placeholder="https://apps.apple.com/..."
              />
            </FormGroup>
          )}

          {platform === 'android' && (
            <FormGroup theme={theme}>
              <Label theme={theme}>Play Store URL</Label>
              <Input
                theme={theme}
                type="url"
                value={playStoreUrl}
                onChange={(e) => setPlayStoreUrl(e.target.value)}
                placeholder="https://play.google.com/..."
              />
            </FormGroup>
          )}

          <div style={{ display: 'flex', gap: getTheme(isDark).spacing.sm, marginTop: getTheme(isDark).spacing.lg }}>
            <Button
              type="submit"
              variant="primary"
              theme={theme}
              style={{ flex: 1 }}
              disabled={saving}
            >
              {saving ? 'Saxlanılır...' : 'Saxla'}
            </Button>
            <Button
              type="button"
              variant="outline"
              theme={theme}
              onClick={onClose}
              disabled={saving}
            >
              Ləğv et
            </Button>
          </div>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AppVersionForm;

